/**
 * Transclusion - Helpers
 * ======================
 *
 * Utilidades para detectar contenido transcluido en componentes con
 * `transclude: true` (forma booleana, no multi-slot).
 */

import type { ITranscludeFunction, IScope, IAugmentedJQuery } from 'angular';

/**
 * Detecta si el consumidor transcluyó contenido real (elementos o texto no vacío).
 *
 * Necesario porque `isSlotFilled('')` NO funciona con `transclude: true` booleano
 * en AngularJS 1.8.3: $$slots solo se puebla para la forma multi-slot. Se sondea
 * el contenido con un clone descartable y se destruye su scope.
 *
 * Notas importantes para quien toque esto:
 * (a) Costo aceptado: el contenido transcluido queda compilado/linkeado DOS
 *     veces por instancia del componente padre — una vez acá (el probe
 *     descartable, destruido de inmediato) y otra vez en el link real que
 *     hace Angular al renderizar `ng-transclude`. Es un doble costo de
 *     compilación asumido a cambio de poder detectar contenido real con
 *     `transclude: true` booleano.
 * (b) Los hijos compound transcluidos DEBEN declarar su `require` al padre
 *     como opcional (`^?`, p. ej. `require: { card: '^?ecCard' }`) y guardear
 *     su uso (`if (this.card) { ... }`). El clone que arma el probe de acá
 *     está DETACHED (fuera del DOM real), así que un ancestro `^ecCard` no
 *     resuelve ahí; con require no-opcional Angular lanza `$compile:ctreq`
 *     dentro del probe y el error queda silenciado en $exceptionHandler,
 *     matando el modo compound sin aviso visible. Como el probe destruye su
 *     scope de inmediato, los `registerX` de los hijos NO se disparan dos
 *     veces aunque el contenido se linkee dos veces.
 */
export function hasTranscludedContent($transclude: ITranscludeFunction | null | undefined): boolean {
  if (!$transclude) {
    return false;
  }

  let has = false;
  $transclude((clone?: IAugmentedJQuery, scope?: IScope) => {
    if (clone) {
      for (let i = 0; i < clone.length; i++) {
        const node = clone[i];
        const esElemento = node.nodeType === 1;
        const esTextoNoVacio = node.nodeType === 3 && (node.textContent || '').trim() !== '';
        if (esElemento || esTextoNoVacio) {
          has = true;
          break;
        }
      }
    }
    if (scope) {
      scope.$destroy();
    }
  });
  return has;
}
